/* ==========================================================================
   everaw-gesture.js — V19
   ==========================================================================

   The motion primitives for anything a finger can drag: a spring integrator, a
   pointer tracker that knows its own velocity, momentum projection, and
   rubber-banding.

   WHY THIS EXISTS AT ALL
   ----------------------
   A CSS transition has a fixed duration and a fixed curve. That is fine for
   something that merely appears. It is wrong for anything you can grab,
   because it cannot be interrupted: if a user catches a closing drawer
   mid-flight, a CSS transition makes them wait for it to finish before it will
   respond. The drawer stops feeling like an object and starts feeling like a
   video of an object.

   A spring has no duration. Re-targeting it mid-flight is the normal case, not
   an exception -- it just keeps integrating from wherever it currently is, at
   whatever velocity it currently has. That single property is what makes an
   interface feel physical, and it is why this file is ~4 KB of hand-rolled
   maths rather than a call into a library.

   WHY NO LIBRARY
   --------------
   The theme loads no animation library, and this file does not bring one in.
   A library would arrive from a CDN, and a CDN script is not reliably present
   when someone taps the hamburger two seconds after load. Chrome must not
   wait on a CDN script.

   No dependencies. No build step. Attaches one global: window.EverawGesture.
   ========================================================================== */

(function (window, document) {
  'use strict';

  if (window.EverawGesture) return;

  /* Honour the user's motion preference at the source, so callers do not each
     have to remember to. When reduced motion is on, springs snap to their
     target on the next frame instead of travelling. */
  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return reduceMotionQuery.matches;
  }

  /* ------------------------------------------------------------------------
     Spring
     ------------------------------------------------------------------------
     Parameterised the way Apple parameterises springs for designers, not the
     way a physics textbook does. Two numbers:

       damping  -- 1.0 is critically damped: fast, graceful, NO overshoot.
                   Below 1.0 overshoots and oscillates. Lower = bouncier.
       response -- roughly how long (seconds) it takes to reach the target.
                   This is NOT a duration; a spring has no duration. It is a
                   stiffness expressed in units a human can reason about.

     House defaults:
       damping 1.0 for anything that was TAPPED  (a tap carries no momentum,
                                                  so overshoot would be a lie)
       damping ~0.8 for anything that was FLICKED (the momentum is real, so
                                                   a little overshoot reads
                                                   as physical)

     Converting to the standard second-order form:
       omega0 (natural frequency) = 2*PI / response
       stiffness                  = omega0^2
       damping coefficient        = 2 * dampingRatio * omega0
     ------------------------------------------------------------------------ */
  function Spring(options) {
    options = options || {};
    this.damping = options.damping == null ? 1.0 : options.damping;
    this.response = options.response == null ? 0.35 : options.response;
    this.value = options.from || 0;
    this.target = options.to == null ? this.value : options.to;
    this.velocity = options.velocity || 0;

    this.onUpdate = options.onUpdate || null;
    this.onRest = options.onRest || null;

    /* Rest thresholds. Expressed in the same units as `value` (px for our
       callers). Too tight and the spring never formally settles and we burn
       frames; too loose and it visibly snaps the last pixel. */
    this.restDelta = options.restDelta || 0.1;
    this.restVelocity = options.restVelocity || 0.5;

    this._rafId = null;
    this._lastTime = 0;
    this._accumulator = 0;
    this._tick = this._tick.bind(this);
  }

  /* Fixed-timestep integration. Variable dt straight from rAF makes a stiff
     spring explode when a frame is dropped or the tab is backgrounded, which
     shows up as the drawer flying off screen after a stall. Accumulate real
     time and consume it in fixed 1/240s steps instead. */
  Spring.SUB_STEP = 1 / 240;
  Spring.MAX_FRAME = 1 / 20; /* never integrate more than 50ms of stall */

  Spring.prototype.setTarget = function (target, velocity) {
    this.target = target;
    if (velocity != null) this.velocity = velocity;
    this.start();
    return this;
  };

  /* Interrupt handling. The critical rule: a new animation must begin from the
     PRESENTATION value -- where the thing actually is on screen right now --
     never from the logical target. Starting from the target is what produces a
     visible jump when a user grabs something mid-flight.

     Because `this.value` is always the live on-screen value, simply re-targeting
     satisfies this for free, and the existing velocity carries through rather
     than being hard-cut to zero. That continuity is what stops a reversal from
     feeling like it hit a brick wall. */
  Spring.prototype.setValue = function (value, velocity) {
    this.value = value;
    if (velocity != null) this.velocity = velocity;
    return this;
  };

  Spring.prototype.start = function () {
    if (this._rafId != null) return this;

    if (prefersReducedMotion()) {
      /* Non-vestibular equivalent: arrive, but do not travel. */
      this.value = this.target;
      this.velocity = 0;
      if (this.onUpdate) this.onUpdate(this.value, this.velocity);
      if (this.onRest) this.onRest(this.value);
      return this;
    }

    this._lastTime = 0;
    this._accumulator = 0;
    this._rafId = window.requestAnimationFrame(this._tick);
    return this;
  };

  Spring.prototype.stop = function () {
    if (this._rafId != null) {
      window.cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    return this;
  };

  Spring.prototype.isAnimating = function () {
    return this._rafId != null;
  };

  Spring.prototype._tick = function (now) {
    if (!this._lastTime) this._lastTime = now;
    var frame = (now - this._lastTime) / 1000;
    this._lastTime = now;

    if (frame > Spring.MAX_FRAME) frame = Spring.MAX_FRAME;
    this._accumulator += frame;

    var omega0 = (2 * Math.PI) / this.response;
    var k = omega0 * omega0;
    var c = 2 * this.damping * omega0;
    var step = Spring.SUB_STEP;

    while (this._accumulator >= step) {
      var displacement = this.value - this.target;
      var accel = -k * displacement - c * this.velocity;
      this.velocity += accel * step;
      this.value += this.velocity * step;
      this._accumulator -= step;
    }

    if (this.onUpdate) this.onUpdate(this.value, this.velocity);

    var settled =
      Math.abs(this.value - this.target) < this.restDelta &&
      Math.abs(this.velocity) < this.restVelocity;

    if (settled) {
      this.value = this.target;
      this.velocity = 0;
      this._rafId = null;
      if (this.onUpdate) this.onUpdate(this.value, this.velocity);
      if (this.onRest) this.onRest(this.value);
      return;
    }

    this._rafId = window.requestAnimationFrame(this._tick);
  };

  /* ------------------------------------------------------------------------
     Momentum projection
     ------------------------------------------------------------------------
     Where would this come to rest if the user let go right now and it
     decelerated like a scroll view? Snap decisions should be made against that
     PROJECTED point, not against the position at the moment of release --
     that is the difference between a flick that throws something and a flick
     that politely returns it.

     This is the exponential-decay form Apple ships in the Designing Fluid
     Interfaces sample code. It is NOT the textbook v^2/(2a); using that gives
     noticeably wrong distances at low velocity.

     velocity is px/s. 0.998 matches normal scroll feel; 0.99 is snappier.
     ------------------------------------------------------------------------ */
  function project(velocity, decelerationRate) {
    var d = decelerationRate == null ? 0.998 : decelerationRate;
    return ((velocity / 1000) * d) / (1 - d);
  }

  /* ------------------------------------------------------------------------
     Rubber-banding
     ------------------------------------------------------------------------
     Past a boundary, resist progressively rather than stopping dead. A hard
     stop reads as "frozen, possibly broken"; increasing resistance reads as
     "responsive, but there is nothing more this way".
     ------------------------------------------------------------------------ */
  function rubberband(overshoot, dimension, constant) {
    var c = constant == null ? 0.55 : constant;
    if (!dimension) return 0;
    return (overshoot * dimension * c) / (dimension + c * Math.abs(overshoot));
  }

  /* ------------------------------------------------------------------------
     DragTracker
     ------------------------------------------------------------------------
     Pointer Events with capture, a short velocity history, a movement
     threshold before committing to an axis, and correct grab-offset handling.

     Three details that are easy to get wrong and very visible when you do:

     1. setPointerCapture. Without it, tracking dies the moment the finger
        leaves the element's box -- which for a drawer being swiped off screen
        is immediately.

     2. Velocity from a HISTORY, not from the last two events. A single
        pointermove pair is extremely noisy, and on release that noise becomes
        the throw velocity. We keep ~80ms of samples and measure across them.

     3. The grab offset. The element must move relative to where it was
        actually grabbed. Snapping its edge to the finger on first move is the
        single most common tell of a hand-rolled drag.
     ------------------------------------------------------------------------ */
  function DragTracker(element, options) {
    options = options || {};
    this.element = element;
    this.axis = options.axis || 'x';
    this.threshold = options.threshold == null ? 8 : options.threshold;

    this.onStart = options.onStart || null;
    this.onMove = options.onMove || null;
    this.onEnd = options.onEnd || null;
    /* Return false to decline a gesture (e.g. the touch began on a scrolled
       region that should scroll instead of drag). */
    this.canStart = options.canStart || null;

    this._active = false;
    this._committed = false;
    this._pointerId = null;
    this._startX = 0;
    this._startY = 0;
    this._history = [];

    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);

    /* passive:true on pointerdown -- we never preventDefault there, and saying
       so lets the browser start scrolling without waiting on us. */
    element.addEventListener('pointerdown', this._onDown, { passive: true });
  }

  DragTracker.HISTORY_MS = 80;

  DragTracker.prototype.destroy = function () {
    this.element.removeEventListener('pointerdown', this._onDown);
    this._detachMoveListeners();
  };

  DragTracker.prototype._attachMoveListeners = function () {
    /* passive:false because a committed horizontal drag must be able to
       preventDefault to stop the page scrolling underneath it. */
    this.element.addEventListener('pointermove', this._onMove, { passive: false });
    this.element.addEventListener('pointerup', this._onUp);
    this.element.addEventListener('pointercancel', this._onUp);
  };

  DragTracker.prototype._detachMoveListeners = function () {
    this.element.removeEventListener('pointermove', this._onMove);
    this.element.removeEventListener('pointerup', this._onUp);
    this.element.removeEventListener('pointercancel', this._onUp);
  };

  DragTracker.prototype._onDown = function (event) {
    if (this._active) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.canStart && this.canStart(event) === false) return;

    this._active = true;
    this._committed = false;
    this._pointerId = event.pointerId;
    this._startX = event.clientX;
    this._startY = event.clientY;
    this._history = [{ x: event.clientX, y: event.clientY, t: event.timeStamp }];

    this._attachMoveListeners();
  };

  DragTracker.prototype._onMove = function (event) {
    if (!this._active || event.pointerId !== this._pointerId) return;

    var dx = event.clientX - this._startX;
    var dy = event.clientY - this._startY;

    this._history.push({ x: event.clientX, y: event.clientY, t: event.timeStamp });
    var cutoff = event.timeStamp - DragTracker.HISTORY_MS;
    while (this._history.length > 2 && this._history[0].t < cutoff) {
      this._history.shift();
    }

    if (!this._committed) {
      var primary = this.axis === 'x' ? Math.abs(dx) : Math.abs(dy);
      var secondary = this.axis === 'x' ? Math.abs(dy) : Math.abs(dx);
      if (primary < this.threshold && secondary < this.threshold) return;

      /* Both plausible gestures were tracked from the first move; now that
         intent is clear, confidently cancel the loser. If the user went across
         our axis, this is a scroll, not a drag -- let go of it entirely. */
      if (secondary > primary) {
        this._active = false;
        this._detachMoveListeners();
        return;
      }

      this._committed = true;
      /* Capture only once we own the gesture, so a vertical scroll that merely
         began here is never stolen from the page. */
      if (this.element.setPointerCapture) {
        try {
          this.element.setPointerCapture(this._pointerId);
        } catch (e) {
          /* Safari throws if the pointer is already gone. Not fatal. */
        }
      }
      if (this.onStart) this.onStart({ x: this._startX, y: this._startY, event: event });
    }

    if (event.cancelable) event.preventDefault();

    if (this.onMove) {
      this.onMove({ dx: dx, dy: dy, delta: this.axis === 'x' ? dx : dy, event: event });
    }
  };

  /* Measure velocity across the retained history rather than the final pair.
     Units: px/s, signed along the tracked axis. */
  DragTracker.prototype._velocity = function () {
    var h = this._history;
    if (h.length < 2) return 0;
    var first = h[0];
    var last = h[h.length - 1];
    var dt = (last.t - first.t) / 1000;
    if (dt <= 0) return 0;
    var distance = this.axis === 'x' ? last.x - first.x : last.y - first.y;
    return distance / dt;
  };

  DragTracker.prototype._onUp = function (event) {
    if (!this._active || event.pointerId !== this._pointerId) return;

    var wasCommitted = this._committed;
    var velocity = this._velocity();
    var dx = event.clientX - this._startX;
    var dy = event.clientY - this._startY;

    this._active = false;
    this._committed = false;
    this._detachMoveListeners();

    if (this.element.releasePointerCapture) {
      try {
        this.element.releasePointerCapture(this._pointerId);
      } catch (e) {
        /* already released */
      }
    }
    this._pointerId = null;

    if (wasCommitted && this.onEnd) {
      this.onEnd({
        dx: dx,
        dy: dy,
        delta: this.axis === 'x' ? dx : dy,
        velocity: velocity,
        projected: project(velocity),
        event: event
      });
    }
  };

  window.EverawGesture = {
    Spring: Spring,
    DragTracker: DragTracker,
    project: project,
    rubberband: rubberband,
    prefersReducedMotion: prefersReducedMotion
  };
})(window, document);
